import { PHASE, PLAYER } from "../../../common/constants.ts";
import type { UpdateEvents } from "../../../common/types.ts";
import { team } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g } from "../util/index.ts";
import { getNegotiationPids } from "./negotiationList.ts";

const isDraftPhase = (phase: number) =>
  phase === PHASE.EXPANSION_DRAFT ||
  phase === PHASE.FANTASY_DRAFT ||
  phase === PHASE.DRAFT ||
  phase === PHASE.AFTER_DRAFT;

const isFreeAgencyAvailable = (phase: number) =>
  !(
    (phase > PHASE.AFTER_TRADE_DEADLINE && phase <= PHASE.RESIGN_PLAYERS) ||
    phase === PHASE.FANTASY_DRAFT ||
    phase === PHASE.EXPANSION_DRAFT
  );

const isTradeAvailable = (phase: number) =>
  phase < PHASE.AFTER_TRADE_DEADLINE && !isDraftPhase(phase);

export default async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    !updateEvents.includes("firstRun") &&
    !updateEvents.includes("gameSim") &&
    !updateEvents.includes("newPhase") &&
    !updateEvents.includes("playerMovement")
  ) {
    return;
  }

  const phase = g.get("phase");
  const season = g.get("season");
  const userTid = g.get("userTid");
  const maxRosterSize = g.get("maxRosterSize");
  const minRosterSize = g.get("minRosterSize");
  const salaryCap = g.get("salaryCap") / 1000;
  const luxuryPayroll = g.get("luxuryPayroll") / 1000;
  const salaryCapType = g.get("salaryCapType");
  const expansionDraft = g.get("expansionDraft");

  const [userPlayers, freeAgents, negotiationPids, payroll] = await Promise.all([
    idb.cache.players.indexGetAll("playersByTid", userTid),
    idb.cache.players.indexGetAll("playersByTid", PLAYER.FREE_AGENT),
    getNegotiationPids(userTid),
    team.getPayroll(userTid),
  ]);

  const payrollM = payroll / 1000;
  const payrollLimit = salaryCapType === "none" ? luxuryPayroll : salaryCap;
  const rosterCount = userPlayers.length;
  const expansionProtectionActive = expansionDraft.phase === "protection";
  const expansionTeam =
    "expansionTids" in expansionDraft && expansionDraft.expansionTids.includes(userTid);
  const needsExpansionProtection = expansionProtectionActive && !expansionTeam;

  return {
    marketState: {
      canSignFreeAgents: isFreeAgencyAvailable(phase),
      canTrade: isTradeAvailable(phase),
      hasPendingNegotiations: negotiationPids.size > 0,
      needsExpansionProtection,
    },
    phase,
    teamMarketContext: {
      capSpace: payrollLimit - payrollM,
      contractExpiringCount: userPlayers.filter((player) => player.contract.exp <= season).length,
      freeAgentsAvailable: freeAgents.length,
      injuredPlayers: userPlayers.filter((player) => player.injury.gamesRemaining > 0).length,
      maxRosterSize,
      minRosterDeficit: Math.max(0, minRosterSize - rosterCount),
      minRosterSize,
      openRosterSpots: Math.max(0, maxRosterSize - rosterCount),
      payroll: payrollM,
      payrollLimit,
      pendingNegotiations: negotiationPids.size,
      rosterCount,
      salaryCapType,
    },
  };
};
